import React from 'react';
import { WeezHeader, Button, SplitContainer, Message, Icon, FrameCard, ToastContext } from '@weezevent/nacre';
import Category from '../../../../models/category';
import LoadingCard from '../../../../components/loadingCard';
import { useForm, fetcher } from '@weezevent/weezjs-core';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CategoryForm from './CategoryForm';
import css from '../../chart-list.module.css';

function EditCategory({ organization, chartId, categoryId }) {
    const { t } = useTranslation();
    const history = useHistory();
    const toast = React.useContext(ToastContext);
    const [submitting, setSubmitting] = React.useState(false);

    const options = ['fr_FR', 'fr_CA', 'en_CA', 'en_US', 'en_GB', 'it_IT', 'es_ES'];

    const [form, setForm] = useForm({
        categories: []
    });

    const [category, loading, sync] = Category.useApiModel(
        {
            organizationId: organization.id,
            chartId,
            id: categoryId,
            cache: false,
            onSuccess: cat => {
                setForm('categories', [{ ...cat.object }]);
            }
        },
        [organization, chartId, categoryId]
    );

    const handleReset = React.useCallback(() => {
        sync();
    }, [sync]);

    const handleBack = React.useCallback(() => {
        history.push(`/ticket/O${organization.id}/charts/${chartId}/categories`);
    }, [organization, chartId]);

    const handleSubmit = React.useCallback(() => {
        setSubmitting(true);
        fetcher
            .patch(`/ticket/organizations/${organization.id}/charts/${chartId}/categories/${categoryId}`, { data: form.categories[0] })
            .then(() => {
                toast.success(t(`ticket.charts.category.creationSuccess`));
                history.push(`/ticket/O${organization.id}/charts/${chartId}/categories`);
            })
            .catch(e => {
                toast.error(t(`ticket.charts.category.creationError}`));
            })
            .finally(() => {
                setSubmitting(false);
            });
    }, [form.categories, organization, chartId]);

    const disableSubmit = React.useMemo(() => {
        return form.categories[0]?.name === '';
    }, [form.categories]);

    if (!category) {
        return null;
    }

    return (
        <>
            <WeezHeader
                backLink={{
                    onClick: () => handleBack()
                }}
                rightComponent={[
                    <Button
                        key="Delete"
                        className={css['category-reset-btn']}
                        onClick={handleReset}
                        label={
                            <>
                                <Icon name="Reset" /> <span>{t('ticket.common.action.reset')}</span>
                            </>
                        }
                    />,
                    <Button
                        key="Display"
                        className={css['category-save-btn']}
                        label={
                            <>
                                <Icon className={css['category-save-mobile-icon']} name="save" size="regular" />
                                <span className={css['category-save-mobile-label']}>{t('ticket.common.action.save')}</span>
                            </>
                        }
                        disabled={disableSubmit}
                        onClick={handleSubmit}
                        primary
                    />
                ]}
                title={category.name}
            />

            <SplitContainer
                additional={
                    <Message type="tips" title={t('notifications.tips_title')} mini>
                        <p>{t('notifications.category_tips')}</p>
                    </Message>
                }
            >
                <FrameCard title={t('ticket.creationEvent.stepSideBar.stepInfos')} subtitle={t('ticket.charts.newCategory.informations')}>
                    <LoadingCard loading={!category || loading || submitting}>
                        <CategoryForm index={0} organization={organization} options={options} category={form.categories[0]} form={form} setForm={setForm} />
                    </LoadingCard>
                </FrameCard>
            </SplitContainer>
        </>
    );
}

export default EditCategory;
